import { getToken, onMessage } from 'firebase/messaging';
import { getFirebaseMessaging } from '../firebase';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const FIREBASE_SW_PATH = '/firebase-messaging-sw.js';

function getAuthToken() {
  return (
    localStorage.getItem('token') ||
    localStorage.getItem('accessToken') ||
    localStorage.getItem('authToken')
  );
}

async function getFirebaseServiceWorkerRegistration() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service worker non supportato da questo browser.');
  }

  const registrations = await navigator.serviceWorker.getRegistrations();

  const existingRegistration = registrations.find((registration) =>
    registration.active?.scriptURL?.endsWith(FIREBASE_SW_PATH)
  );

  if (existingRegistration) {
    // Una registrazione che esiste gia' non basta: va chiesto al browser di
    // ricontrollare lo script, altrimenti resta attivo il service worker
    // installato la prima volta. E' cosi' che chi aveva attivato le
    // notifiche push si ritrovava senza la cache offline aggiunta dopo.
    try {
      await existingRegistration.update();
    } catch (err) {
      console.warn('Aggiornamento del service worker non riuscito:', err);
    }

    return existingRegistration;
  }

  const registration = await navigator.serviceWorker.register(FIREBASE_SW_PATH);

  await navigator.serviceWorker.ready;

  return registration;
}

/**
 * Registra il service worker senza chiedere il permesso per le notifiche.
 *
 * Serve per la cache offline: prima veniva registrato solo da chi attivava
 * le notifiche push, quindi chi non le voleva restava anche senza la
 * possibilita' di aprire l app senza rete. Registrare non chiede niente
 * all utente: il permesso e un passo separato.
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;

  try {
    return await getFirebaseServiceWorkerRegistration();
  } catch (err) {
    console.warn('Service worker non registrato:', err);
    return null;
  }
}

export async function requestAndSaveNotificationToken() {
  if (!('Notification' in window)) {
    throw new Error('Le notifiche non sono supportate da questo browser.');
  }

  if (!('serviceWorker' in navigator)) {
    throw new Error('Service worker non supportato da questo browser.');
  }

  let permission = Notification.permission;

  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }

  if (permission !== 'granted') {
    throw new Error('Permesso notifiche non concesso.');
  }

  const messaging = await getFirebaseMessaging();

  if (!messaging) {
    throw new Error('Firebase Messaging non supportato su questo browser.');
  }

  const registration = await getFirebaseServiceWorkerRegistration();

  const fcmToken = await getToken(messaging, {
    vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
    serviceWorkerRegistration: registration,
  });

  if (!fcmToken) {
    throw new Error('Token notifiche non generato.');
  }

  const accessToken = getAuthToken();

  const response = await fetch(`${API_BASE_URL}/notifications/register-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({
      token: fcmToken,
      platform: 'web-pwa',
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Errore durante il salvataggio del token notifiche.');
  }

  return fcmToken;
}

export async function listenForegroundMessages(callback) {
  const messaging = await getFirebaseMessaging();

  if (!messaging) {
    return () => {};
  }

  return onMessage(messaging, (payload) => {
    callback(payload);
  });
}